import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContentCard } from "../../../components/ui";
import { resolveClaimActions } from "../../../services/conveyanceClaimService";
import { hasApprovalHistory } from "../constants/claimConstants";
import { ClaimAuditTrail } from "./ClaimAuditTrail";

/**
 * The approval history on the submitter's own claim (ESS).
 *
 * The same card, the same rows and the same component the approver sees on
 * Screen 6 — every action taken on the claim, oldest first, each with the
 * comment recorded against it. A submitter whose claim was sent back or
 * rejected reads the reason here, on the claim, rather than being told only
 * that it happened.
 *
 * The trail arrives on the claim the page has already loaded, so this card
 * normally costs no request of its own — see resolveClaimActions.
 *
 * It renders only once the claim has a history to have: a claim nobody has
 * acted on yet shows nothing at all, rather than an empty card above a form
 * the employee is still filling in.
 */
export function ClaimApprovalHistory({ claim }) {
  const { t } = useTranslation();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!claim) return undefined;
    let active = true;

    setLoading(true);
    resolveClaimActions(claim).then((rows) => {
      if (!active) return;
      setActions(rows || []);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [claim]);

  if (!claim) return null;
  if (actions.length === 0 && !hasApprovalHistory(claim.claimStatus)) return null;

  return (
    <ContentCard title={t("conveyanceApproval.auditTrail")}>
      <ClaimAuditTrail actions={actions} loading={loading} />
    </ContentCard>
  );
}

export default ClaimApprovalHistory;
